// AdvancedSettings.tsx
import React, { useState,useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { calculateLuminance, getContrastRatio, MIN_CONTRAST_RATIO } from '../utils/LuminosityChecker';
import { uploadFile } from '../services/api/virtualAssistant'; // Import the uploadFile function

interface AdvancedUISettingsProps {
  isUIModalOpen: boolean;
  setIsUIModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  bgColor: string;
  setBgColor: React.Dispatch<React.SetStateAction<string>>;
  fontColor: string;
  setFontColor: React.Dispatch<React.SetStateAction<string>>;
  iconColor: string;
  setIconColor: React.Dispatch<React.SetStateAction<string>>;
  isShowHeader: boolean;
  setIsShowHeader: React.Dispatch<React.SetStateAction<boolean>>;
  headerColor: string;
  setHeaderColor: React.Dispatch<React.SetStateAction<string>>;
  logo: string;
  setLogo: React.Dispatch<React.SetStateAction<string>>;
}

const AdvancedUISettings: React.FC<AdvancedUISettingsProps> = ({
  isUIModalOpen,
  setIsUIModalOpen,
  bgColor,
  setBgColor,
  fontColor,
  setFontColor,
  iconColor,
  setIconColor,
  isShowHeader,
  setIsShowHeader,
  headerColor,
  setHeaderColor,
  logo,
  setLogo,
}) => {
  const [fontWarning, setFontWarning] = useState<string | null>(null);
  const [iconWarning, setIconWarning] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [textBgInputValue, setTextBgInputValue] = useState(bgColor);
  const [colorBgError, setColorBgError] = useState('');
  const [fontTextInputValue, setFontTextInputValue] = useState(fontColor);
  const [fontColorError, setFontColorError] = useState('');
  const [headerColorError, setHeaderColorError] = useState('');
  const [iconTextInputValue, setIconTextInputValue] = useState(iconColor);
  const [iconColorError, setIconColorError] = useState('');
  const [headerTextInputValue, setHeaderTextInputValue] = useState(headerColor);
  const [isTextInputActive, setIsTextInputActive] = useState(false);
  const [isFontTextInputActive, setIsFontTextInputActive] = useState(false);
  const [isIconTextInputActive, setIsIconTextInputActive] = useState(false);
  const [isHeaderTextInputActive, setIsHeaderTextInputActive] = useState(false);
  useEffect(() => {
  // Only sync the text input when user is not actively editing it
  if (!isTextInputActive) {
    setTextBgInputValue(bgColor);
  }
}, [bgColor, isTextInputActive]);

  useEffect(() => {
    if(!isFontTextInputActive){
    setFontTextInputValue(fontColor);
    }
  },[fontColor,isFontTextInputActive]);

  useEffect(() => {
    if(!isIconTextInputActive){
    setIconTextInputValue(iconColor);
    }
  },[iconColor,isIconTextInputActive]);

  useEffect(() => {
    if(!isHeaderTextInputActive){
    setHeaderTextInputValue(headerColor);
    }
  },[headerColor,isHeaderTextInputActive]);

  // Helper function to validate color formats
  const isValidColor = (color: string): boolean => {
    // Check if it's a valid hex color (3 or 6 digits)
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (hexRegex.test(color)) {
      return true;
    }
    
    // Check if it's a valid CSS color name or rgb/rgba value
    const tempDiv = document.createElement('div');
    tempDiv.style.color = color;
    return tempDiv.style.color !== '';
  };

  // Helper function to convert any valid color to hex format
  const convertToHex = (color: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.style.color = color;
    document.body.appendChild(tempDiv);
    const computedColor = window.getComputedStyle(tempDiv).color;
    document.body.removeChild(tempDiv);
    
    // Convert rgb(r, g, b) to hex
    const rgbMatch = computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]);
      const g = parseInt(rgbMatch[2]);
      const b = parseInt(rgbMatch[3]);
      return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
    }
    
    return color;
  };
  const handleBgColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setColorBgError('')
    const newBgColor = event.target.value;
    let newFontColor = fontColor;
    if (getContrastRatio(newBgColor, fontColor) < MIN_CONTRAST_RATIO) {
      newFontColor = calculateLuminance(newBgColor) > 0.5 ? "#000000" : "#ffffff";
    }
    setBgColor(newBgColor);
    setFontColor(newFontColor);
  };
  
const handleBgTextColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  const inputValue = event.target.value;
  setTextBgInputValue(inputValue); // Always update the input value immediately

  if (isValidColor(inputValue)) {
    setColorBgError('');
    const hexColor = convertToHex(inputValue);
    
    // Only update bgColor if it's actually different to prevent unnecessary re-renders
    if (hexColor !== bgColor) {
      setBgColor(hexColor);
    }
 
    // Update font color based on contrast
    let newFontColor = fontColor;
    if (getContrastRatio(hexColor, fontColor) < MIN_CONTRAST_RATIO) {
      newFontColor = calculateLuminance(hexColor) > 0.5 ? "#000000" : "#ffffff";
    }
    setFontColor(newFontColor);
  } else if (inputValue.trim() !== '') {
    setColorBgError('Please enter a valid color (hex, rgb, or color name)');
  } else {
    setColorBgError('');
  }
};

const handleFontTextColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  
  const inputValue = event.target.value;
  setFontTextInputValue(inputValue);
  
  if (isValidColor(inputValue)) {
    setFontColorError('');
    const hexColor = convertToHex(inputValue);
    if (hexColor !== fontColor) {
      setFontColor(hexColor);
    }
    // setIconColor(hexColor);
    // setHeaderColor(hexColor);
    let newBgColor = bgColor;
    // Check contrast and set warning if needed
    if (getContrastRatio(hexColor, bgColor) < MIN_CONTRAST_RATIO) {
        newBgColor = calculateLuminance(hexColor) > 0.5 ? "#000000" : "#ffffff";
      }
      setBgColor(newBgColor);
  } else if (inputValue.trim() !== '') {
    setFontColorError('Please enter a valid color (hex, rgb, or color name)');
  } else {
    setFontColorError('');
  }
};

  

  const handleFontColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFontColorError('')
    const newFontColor = event.target.value;
    let newBgColor = bgColor;

    if (getContrastRatio(newFontColor, bgColor) < MIN_CONTRAST_RATIO) {
      newBgColor = calculateLuminance(newFontColor) > 0.5 ? "#000000" : "#ffffff";
    }

    setFontColor(newFontColor);
    setBgColor(newBgColor);
    // setIconColor(newFontColor);
    // setHeaderColor(newFontColor);
  };

  const handleIconTextColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  const inputValue = event.target.value;
  setIconTextInputValue(inputValue);
  
  if (isValidColor(inputValue)) {
    setIconColorError('');
    const hexColor = convertToHex(inputValue);
    if (hexColor !== iconColor) {
      setIconColor(hexColor);
    }
  } else if (inputValue.trim() !== '') {
    setIconColorError('Please enter a valid color (hex, rgb, or color name)');
  } else {
    setIconColorError('');
  }
};

// New handler for header color text input changes
const handleHeaderTextColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  const inputValue = event.target.value;
  setHeaderTextInputValue(inputValue);
  
  if (isValidColor(inputValue)) {
    setHeaderColorError('');
    const hexColor = convertToHex(inputValue);
    if (hexColor !== headerColor) {
      setHeaderColor(hexColor);
    }
  } else if (inputValue.trim() !== '') {
    setHeaderColorError('Please enter a valid color (hex, rgb, or color name)');
  } else {
    setHeaderColorError('');
  }
};

  const handleIconColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newIconColor = event.target.value;
    setIconColor(newIconColor);
  };

  const handleHeaderColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHeaderColor(event.target.value);
  };

  const handleOverlayClick = () => {
    setIsUIModalOpen(false);
  };

  const handleModalClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (!file.type.startsWith('image/')) {
        console.error('Only image files are allowed.');
        return;
      }
      setIsUploadingLogo(true);

      try {
        const { fileLocation } = await uploadFile(file, 'logo', 'temp',localStorage.getItem('netId') || '');
        setLogo(fileLocation); // Update the image source with the uploaded file location
      } catch (error) {
        console.error('Error uploading file:', error);
      } finally {
        setIsUploadingLogo(false); // Stop loader
      }
    }
  };

  return (
    <div className={`fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-end z-50 ${isUIModalOpen ? 'block' : 'hidden'}`}
      onClick={handleOverlayClick}>
      <div className="bg-white w-[25%] h-[93%] shadow-lg transform transition-transform duration-300 ease-in-out" onClick={handleModalClick}>
      <div className="h-full flex flex-col">
        <div className="p-6 border-b border-gray-200 bg-white z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Advanced UI Settings</h2>
            <button
              onClick={() => setIsUIModalOpen(false)}
              className="text-black-500 hover:text-red-700"
            >
              <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
            </button>
          </div>
          <p className="sub-heading mt-2">Customize the look-and-feel of your app.</p>
          </div>
          <div className="overflow-y-auto flex-grow px-6 pb-6 pt-4 space-y-6  mr-4  ">
          <div className="pt-2">
            <label htmlFor="bgColorPicker" className="model-label pt-5 pl-2">
              Background Color
            </label>
            <div className="flex items-center pt-2">
              <div className="relative">
                <input
                  type="text"
                  value={textBgInputValue}
                  onChange={handleBgTextColorChange}
                  onFocus={() => setIsTextInputActive(true)}
                    onBlur={() => setIsTextInputActive(false)}
                  className={`border rounded-lg mx-1.5 mt-1 pt-1 pl-2 text-left w-[100px] h-[35px] ${
                    colorBgError ? 'border-red-500' : 'border-gray-400'
                  }`}
                  placeholder="#000000"
                />
                {colorBgError && (
                  <div className="absolute top-full left-1.5 mt-1 text-red-500 text-xs whitespace-nowrap bg-white border border-red-200 rounded px-2 py-1 shadow-sm z-10">
                    {colorBgError}
                  </div>
                )}
              </div>
              <input
                type="color"
                id="bgColorPicker"
                value={bgColor}
                onChange={handleBgColorChange}
                className="h-6 w-6"
              />
            </div>
          </div>

          <div className="pt-3">
            <label htmlFor="fontColorPicker" className="model-label pl-2">
              Font Color
            </label>
            <div className="flex items-center pt-2">
              <div className="relative">
                <input
                  type="text"
                  value={fontTextInputValue}
                  onChange={handleFontTextColorChange}
                  className={`border rounded-lg mx-1.5 mt-1 pt-1 pl-2 text-left w-[100px] h-[35px] ${
                    fontColorError ? 'border-red-500' : 'border-gray-400'
                  }`}
                  placeholder="#000000"
                   onFocus={() => setIsFontTextInputActive(true)}
                    onBlur={() => setIsFontTextInputActive(false)}
                />
                {fontColorError && (
                  <div className="absolute top-full left-1.5 mt-1 text-red-500 text-xs whitespace-nowrap bg-white border border-red-200 rounded px-2 py-1 shadow-sm z-10">
                    {fontColorError}
                  </div>
                )}
              </div>
              <input
                type="color"
                id="fontColorPicker"
                value={fontColor}
                onChange={handleFontColorChange}
                className="h-6 w-6"
              />
            </div>
            {fontWarning && <p className="text-red-500 text-sm">{fontWarning}</p>}
          </div>
          <div className="pt-3">
            <label htmlFor="colorPicker" className="model-label pl-2">
              Icon Color
            </label>
            <div className="flex items-center pt-2">
            <div className="relative">
              <input
                type="text"
                value={iconTextInputValue}
                onChange={handleIconTextColorChange}
                className={`border rounded-lg mx-1.5 mt-1 pt-1 pl-2 text-left w-[100px] h-[35px] ${
                  iconColorError ? 'border-red-500' : 'border-gray-400'
                }`}
                placeholder="#000000"
                onFocus={() => setIsIconTextInputActive(true)}
                onBlur={() => setIsIconTextInputActive(false)}
              />
              {iconColorError && (
                <div className="absolute top-full left-1.5 mt-1 text-red-500 text-xs whitespace-nowrap bg-white border border-red-200 rounded px-2 py-1 shadow-sm z-10">
                  {iconColorError}
                </div>
              )}
            </div>
            <input
              type="color"
              id="iconColorPicker"
              value={iconColor}
              onChange={handleIconColorChange}
              className="h-6 w-6"
            />
          </div>
          </div>
          <div className="pt-3">
            <label htmlFor="colorPicker" className="model-label pl-2">
              Show Header
            </label>
            <div className="flex items-center pt-1">
              <div className="mt-1 ml-2 flex items-center">
                <div className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center cursor-pointer ${String(isShowHeader) === 'true' ? 'bg-black' : 'bg-white'}`}
                  onClick={() => setIsShowHeader(!isShowHeader)}>
                  {isShowHeader && <FontAwesomeIcon icon={faCheck} className="h-4 w-4 text-white" />}
                </div>
              </div>
            </div>
          </div>
          <div className="pt-3">
            <label htmlFor="colorPicker" className="model-label pl-2">
              Header Color
            </label>
             <div className="flex items-center pt-2">
                <div className="relative">
                  <input
                    type="text"
                    value={headerTextInputValue}
                    onChange={handleHeaderTextColorChange}
                    className={`border rounded-lg mx-1.5 mt-1 pt-1 pl-2 text-left w-[100px] h-[35px] ${
                      headerColorError ? 'border-red-500' : 'border-gray-400'
                    }`}
                    placeholder="#000000"
                    onFocus={() => setIsHeaderTextInputActive(true)}
                    onBlur={() => setIsHeaderTextInputActive(false)}
                  />
                  {headerColorError && (
                    <div className="absolute top-full left-1.5 mt-1 text-red-500 text-xs whitespace-nowrap bg-white border border-red-200 rounded px-2 py-1 shadow-sm z-10">
                      {headerColorError}
                    </div>
                  )}
                </div>
                <input
                  type="color"
                  id="headerColorPicker"
                  value={headerColor}
                  onChange={handleHeaderColorChange}
                  className="h-6 w-6"
                />
              </div>
          </div>
          <div className="pt-3">
            <label htmlFor="colorPicker" className="pl-2">
              Logo
            </label>
            <div className="flex items-center pt-2">
              <div className="relative">
                {/* Display the current logo */}
                <img
                  src={logo}
                  alt="Logo"
                  className="w-[250px] h-[100px] object-contain border border-gray-400 rounded-lg cursor-pointer p-2"
                  onClick={() => document.getElementById("logoInput")?.click()}
                />
                {isUploadingLogo && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 rounded-lg">
                    <div className="loader"></div>
                  </div>
                )}
                {/* Hidden file input */}
                <input
                  type="file"
                  id="logoInput"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default AdvancedUISettings;
